import { Info } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface ScopeSectionMeta {
  id: string;
  label: string;
  description: string;
}

interface Props {
  sections: ScopeSectionMeta[];
  selected: string[];
  baseline: string[];   // saved-version scope, used to compute "newly added" highlight
  mode: 'view' | 'edit';
  onToggle: (id: string) => void;
  onResetToDefault?: () => void;
  canResetToDefault?: boolean;
}

export function DataScopePanel({ sections, selected, baseline, mode, onToggle, onResetToDefault, canResetToDefault }: Props) {
  const selectedSet = new Set(selected);
  const baselineSet = new Set(baseline);
  const count = selected.length;

  return (
    <div className={cn(
      'flex flex-col h-full p-3 gap-2 border-r border-gray-200',
      mode === 'edit' ? 'bg-amber-50' : 'bg-gray-50',
    )}>
      <div className="flex justify-between items-center mb-1">
        <span className={cn(
          'text-xs font-bold',
          mode === 'edit' ? 'text-amber-900' : 'text-gray-900',
        )}>Data scope</span>
      </div>
      <div className="flex-1">
        {sections.map((s) => {
          const isSelected = selectedSet.has(s.id);
          const wasInBaseline = baselineSet.has(s.id);
          const newlyAdded = mode === 'edit' && isSelected && !wasInBaseline;

          return (
            <label
              key={s.id}
              data-testid="scope-row"
              data-newly-added={newlyAdded ? 'true' : 'false'}
              className="flex items-center gap-2 py-1 cursor-pointer text-xs"
            >
              <input
                type="checkbox"
                aria-label={s.label}
                checked={isSelected}
                disabled={mode === 'view'}
                onChange={() => onToggle(s.id)}
                className={cn(
                  'h-3.5 w-3.5 rounded border',
                  newlyAdded
                    ? 'accent-green-600'
                    : isSelected
                      ? 'accent-blue-600'
                      : 'accent-gray-400',
                )}
              />
              <span className={cn(
                isSelected ? 'text-gray-900' : 'text-gray-600',
              )}>
                {s.label}
              </span>
              <span aria-label="Section info" title={s.description} className="ml-auto flex items-center">
                <Info className="h-3 w-3 text-gray-400" />
              </span>
            </label>
          );
        })}
      </div>
      <div className="text-[10px] text-gray-600 border-t border-gray-200 pt-2">
        Selected {count} of {sections.length}
      </div>
      {mode === 'edit' && canResetToDefault && onResetToDefault && (
        <button
          type="button"
          onClick={onResetToDefault}
          className="text-[10px] text-blue-600 hover:underline text-left"
        >
          Reset to default
        </button>
      )}
    </div>
  );
}
